import { EntityLockApi } from '@/modules/core/api/entity-lock.api.ts';
import type { TableId } from '@/modules/common/enums/table-id.enum.ts';
import type { EntityLock } from '@/modules/core/types/entity-lock.type.ts';

export const ENTITY_LOCK_EXPIRATION_SECONDS = 600;
export const ENTITY_LOCK_RENEW_INTERVAL_MS = 5 * 60 * 1000;

export type EntityLockKeeperHandlers = {
  isLockedByOther: (lock: EntityLock) => boolean;
  onLost: (lock: EntityLock) => void;
  onRenewError: () => void;
};

type LockSession = {
  tableId: TableId;
  id: number;
  handlers: EntityLockKeeperHandlers;
  timer: ReturnType<typeof setInterval>;
  hasFailed: boolean;
  releaseTabLock: () => void;
};

function getTabLockName(tableId: TableId, id: number) {
  return `entity-lock:${tableId}:${id}`;
}

function getTabLocks() {
  return globalThis.navigator?.locks;
}

function holdTabLock(tableId: TableId, id: number) {
  const locks = getTabLocks();

  if (!locks) {
    return () => undefined;
  }

  const controller = new AbortController();
  let release!: () => void;
  const released = new Promise<void>((resolve) => (release = resolve));

  locks.request(getTabLockName(tableId, id), { signal: controller.signal }, () => released).catch(() => undefined);

  return () => {
    release();
    controller.abort();
  };
}

export class EntityLockKeeper {
  private session: LockSession | null = null;

  static async isHeldInOtherTab(tableId: TableId, id: number) {
    const locks = getTabLocks();

    if (!locks) {
      return false;
    }

    const name = getTabLockName(tableId, id);
    const { held = [], pending = [] } = await locks.query().catch(() => ({ held: [], pending: [] }));

    return [...held, ...pending].some((lock) => lock.name === name);
  }

  start(tableId: TableId, id: number, handlers: EntityLockKeeperHandlers) {
    this.stop();

    this.session = {
      tableId,
      id,
      handlers,
      timer: setInterval(this.renew, ENTITY_LOCK_RENEW_INTERVAL_MS),
      hasFailed: false,
      releaseTabLock: holdTabLock(tableId, id),
    };

    window.addEventListener('pagehide', this.release);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  stop() {
    if (!this.session) {
      return;
    }

    clearInterval(this.session.timer);
    this.session.releaseTabLock();
    this.session = null;

    window.removeEventListener('pagehide', this.release);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  private readonly renew = async () => {
    const session = this.session;

    if (!session) {
      return;
    }

    try {
      const lock = await EntityLockApi.get(session.tableId, session.id);

      if (this.session !== session) {
        return;
      }

      if (session.handlers.isLockedByOther(lock)) {
        this.stop();
        session.handlers.onLost(lock);
        return;
      }

      await EntityLockApi.lock(session.tableId, session.id, ENTITY_LOCK_EXPIRATION_SECONDS);
      session.hasFailed = false;
    } catch {
      if (this.session === session && !session.hasFailed) {
        session.hasFailed = true;
        session.handlers.onRenewError();
      }
    }
  };

  private readonly release = () => {
    if (this.session) {
      EntityLockApi.unlockOnUnload(this.session.tableId, this.session.id);
    }
  };

  private readonly onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      void this.renew();
    }
  };
}
